// Agrégation anonyme des forces (Gallup) et profils Nova pour la vue
// « Forces & profils » de la page Équipes. Module PUR (aucun import Firestore) :
// il ne manipule que des données déjà chargées et ne renvoie que des NOMBRES —
// jamais de nom ni d'email — pour pouvoir être passé sans risque à un composant
// client. Voir components/equipes/forces-profils-panel.tsx.

import {
  GALLUP_STRENGTHS_BY_DOMAIN,
  type GallupDomain,
} from './gallup'
import { NOVA_COLORS, type NovaColor } from './nova'

// Seuil d'anonymat : on n'expose le détail d'une équipe que si elle compte au
// moins ce nombre de personnes ayant renseigné la dimension concernée. Évite
// qu'un filtre sur une équipe d'une ou deux personnes ne révèle un profil
// individuel (la vue est ouverte à tous les rôles).
export const MIN_RESPONDENTS = 3

// Sous-ensemble des champs utilisateur nécessaires à l'agrégation.
export type ProfileUser = {
  active?: boolean
  team_id: string | null
  gallup_strengths: string[]
  nova_red: number | null
  nova_yellow: number | null
  nova_green: number | null
  nova_blue: number | null
}

type TeamRef = { id: string; name: string }

export type ForcesAgg = {
  respondents: number
  domainScores: Record<GallupDomain, number>
  themeScores: Record<string, number>
}

export type NovaAgg = {
  respondents: number
  averages: Record<NovaColor, number>
}

export type ScopeAnalysis = {
  key: string // 'all' pour la vue globale, sinon l'id d'équipe
  label: string
  headcount: number // membres actifs dans le périmètre
  forces: ForcesAgg
  nova: NovaAgg
}

export type ProfileAnalysis = {
  global: ScopeAnalysis
  teams: ScopeAnalysis[] // équipes au-dessus du seuil d'anonymat, triées par nom
  suppressedTeams: number // équipes masquées (trop peu de répondants)
}

// Index inverse thème → domaine, construit une fois.
const DOMAIN_OF: Record<string, GallupDomain> = (() => {
  const map: Record<string, GallupDomain> = {}
  for (const domain of Object.keys(GALLUP_STRENGTHS_BY_DOMAIN) as GallupDomain[]) {
    for (const theme of GALLUP_STRENGTHS_BY_DOMAIN[domain]) {
      map[theme] = domain
    }
  }
  return map
})()

export function domainOfStrength(name: string): GallupDomain | null {
  return DOMAIN_OF[name] ?? null
}

function emptyDomainScores(): Record<GallupDomain, number> {
  return { strategic: 0, influencing: 0, relationship: 0, executing: 0 }
}

// Pointage pondéré par rang : la force #1 pèse 5, la #5 pèse 1. On cumule par
// thème et par domaine sur toutes les personnes ayant renseigné leurs forces.
export function aggregateForces(users: ProfileUser[]): ForcesAgg {
  const domainScores = emptyDomainScores()
  const themeScores: Record<string, number> = {}
  let respondents = 0

  for (const u of users) {
    const list = u.gallup_strengths ?? []
    if (list.length === 0) continue
    respondents++
    list.slice(0, 5).forEach((name, i) => {
      const points = 5 - i // rang 1 → 5, … rang 5 → 1
      themeScores[name] = (themeScores[name] ?? 0) + points
      const domain = domainOfStrength(name)
      if (domain) domainScores[domain] += points
    })
  }

  return { respondents, domainScores, themeScores }
}

// Moyenne de chaque couleur sur les personnes ayant passé le test Nova (au moins
// une valeur non nulle). Une couleur absente compte comme 0 pour ce répondant.
export function aggregateNova(users: ProfileUser[]): NovaAgg {
  const sums: Record<NovaColor, number> = { red: 0, yellow: 0, green: 0, blue: 0 }
  let respondents = 0

  for (const u of users) {
    const values: Record<NovaColor, number | null> = {
      red: u.nova_red,
      yellow: u.nova_yellow,
      green: u.nova_green,
      blue: u.nova_blue,
    }
    const hasAny = NOVA_COLORS.some((c) => values[c] != null)
    if (!hasAny) continue
    respondents++
    for (const c of NOVA_COLORS) sums[c] += values[c] ?? 0
  }

  const averages: Record<NovaColor, number> = { red: 0, yellow: 0, green: 0, blue: 0 }
  if (respondents > 0) {
    for (const c of NOVA_COLORS) averages[c] = sums[c] / respondents
  }

  return { respondents, averages }
}

function buildScope(
  key: string,
  label: string,
  members: ProfileUser[],
): ScopeAnalysis {
  return {
    key,
    label,
    headcount: members.length,
    forces: aggregateForces(members),
    nova: aggregateNova(members),
  }
}

// Construit l'analyse complète : vue globale + une entrée par équipe au-dessus
// du seuil d'anonymat. Ne considère que les membres actifs.
export function buildProfileAnalysis(
  users: ProfileUser[],
  teams: TeamRef[],
): ProfileAnalysis {
  const active = users.filter((u) => u.active !== false)

  const global = buildScope('all', 'Toutes les équipes', active)

  const membersByTeam = new Map<string, ProfileUser[]>()
  for (const u of active) {
    if (!u.team_id) continue
    const list = membersByTeam.get(u.team_id) ?? []
    list.push(u)
    membersByTeam.set(u.team_id, list)
  }

  const teamScopes: ScopeAnalysis[] = []
  let suppressed = 0
  for (const t of teams) {
    const members = membersByTeam.get(t.id) ?? []
    if (members.length === 0) continue
    const scope = buildScope(t.id, t.name, members)
    // Exposée seulement si assez de répondants sur au moins une dimension.
    if (
      scope.forces.respondents >= MIN_RESPONDENTS ||
      scope.nova.respondents >= MIN_RESPONDENTS
    ) {
      teamScopes.push(scope)
    } else {
      suppressed++
    }
  }
  teamScopes.sort((a, b) => a.label.localeCompare(b.label))

  return { global, teams: teamScopes, suppressedTeams: suppressed }
}
